<script lang="ts" module>
  import type { Snapshot } from '@/teoCore/state/TeoStore';
  import type { PromptOptionTemplate } from '@/teoCore/TeoApi';
  import type { DropdownCategoryGrouped } from '@/teoCore/text/generator/GeneratorUiHandler.helpers';
  import { areAllPromptOptionsSelected } from '@/teoCore/text/TeoText.helpers';
  import { useClickOutside } from '@/ui/base/hooks/useClickOutside';
  import LinkButton from '@/ui/elements/buttons/LinkButton.svelte';
  import DropDown from '@/ui/elements/dropdown/DropDown.svelte';
  import type { DropDownItem } from '@/ui/elements/dropdown/DropDown/DropDownSelect.svelte';

  import ConfigCard from './ConfigCard.svelte';
  import ConfigMenuBtn from './ConfigMenuBtn.svelte';

  function templateToDropDownItem(template: Snapshot<PromptOptionTemplate>): DropDownItem {
    return {
      label: template.title,
      value: template.id,
    };
  }

  function toCategory(
    dropdown: Snapshot<DropdownCategoryGrouped>,
    template: PromptOptionTemplate | null,
    selectedCategory: string | null,
    selectedOptions: Record<string, string | undefined>,
  ) {
    const id = dropdown.id;
    const selectedOption = template?.options[id]?.find(
      (option) => String(option.id) === selectedOptions[id],
    );
    return {
      label: selectedOption?.title ?? dropdown.label,
      category: id,
      highlight: !!selectedOption,
      active: selectedCategory === id || selectedCategory === 'ALL',
    };
  }
</script>

<script lang="ts">
  let {
    selectedOptions,
    currentTemplate,
    dropdownCategories,
    selectOption,
    loading,
    templates,
    selectTemplate,
  }: {
    selectedOptions: Record<string, string | undefined>;
    currentTemplate: PromptOptionTemplate | null;
    dropdownCategories: DropdownCategoryGrouped[];
    selectOption: (id: string, value: string) => void;
    loading: boolean;
    templates: PromptOptionTemplate[];
    selectTemplate: (id: string) => void;
  } = $props();

  let singleSelectedTemplate = $derived(
    templates.length === 1 && currentTemplate?.id === templates[0]?.id ? templates[0] : null,
  );

  const initiallySelectedCategory =
    areAllPromptOptionsSelected(currentTemplate, selectedOptions) ? null : 'ALL';

  let categories = $derived.by(() => {
    return dropdownCategories.map((dropdown) =>
      toCategory(dropdown, currentTemplate, selectedCategory, selectedOptions),
    );
  });

  let selectedCategory = $state(initiallySelectedCategory);
  let manuallySelectedCategory = $state(false);

  function unsetSelectedCategory() {
    selectedCategory = null;
    manuallySelectedCategory = false;
  }

  $effect(() => {
    if (!currentTemplate?.id) {
      unsetSelectedCategory();
    } else if (!manuallySelectedCategory) {
      // either the template changed or selected options changed => collapse or expand depending on missing options
      selectedCategory =
        areAllPromptOptionsSelected(currentTemplate, selectedOptions) ? null : 'ALL';
    }
  });

  $effect(() => {
    if (loading) {
      unsetSelectedCategory();
    }
  });

  function toggleOpenAll() {
    selectedCategory = selectedCategory ? null : 'ALL';
    manuallySelectedCategory = !!selectedCategory;
  }

  const closeOnClickOutside = useClickOutside(() => {
    unsetSelectedCategory();
  });

  const handleOptionSelect = (categoryId: string, value: string) => {
    selectOption(categoryId, value);
    if (!manuallySelectedCategory) {
      selectedCategory =
        areAllPromptOptionsSelected(currentTemplate, selectedOptions) ? null : 'ALL';
    }
  };
</script>

<div
  class="fc-xc gap-4 rounded-md bg-plain px-4 pb-3 pt-4 shadow-card transition-all"
  use:closeOnClickOutside
>
  <div class="fc-ns gap-1">
    <div class="fr-csb gap-2 text-lsm font-normal">
      {#if singleSelectedTemplate}
        <span class="px-1 font-medium">{singleSelectedTemplate.title}</span>
      {:else}
        <DropDown
          class=""
          items={templates.map(templateToDropDownItem)}
          selected={currentTemplate?.id ?? ''}
          onItemClick={(val) => selectTemplate(val)}
          ariaLabel="Vorlage auswählen"
          placeholder={loading ? 'Vorlagen werden geladen...' : 'Vorlage auswählen'}
          disabled={loading}
        />
      {/if}
      {#if categories.length}
        <div class="p-1">
          <ConfigMenuBtn onChange={toggleOpenAll} active={!!selectedCategory} />
        </div>
      {/if}
    </div>
    <div class="frw-cs f-1 w-full flex-wrap gap-x-2 text-lsm font-normal">
      {#if categories}
        {#each categories as category, index (category.label)}
          <div class="px-1">
            <LinkButton
              highlight={category.highlight}
              onclick={() => {
                selectedCategory = category.category;
              }}
              active={category.category === selectedCategory}>{category.label}</LinkButton
            >
          </div>

          {#if index !== categories.length - 1}
            <span role="separator">•</span>
          {/if}
        {/each}
      {/if}
    </div>
  </div>

  {#if selectedCategory}
    <div class="fc gap-3 border-t border-c-divider pt-3 transition-all">
      {#each dropdownCategories as dropdown (dropdown.id)}
        {#if selectedCategory === 'ALL' || selectedCategory === dropdown.id}
          <ConfigCard
            groupName={dropdown.id}
            title={dropdown.label}
            groups={dropdown.groups}
            value={selectedOptions[dropdown.id]}
            onchange={(value) => handleOptionSelect(dropdown.id, value)}
          />
        {/if}
      {/each}
    </div>
  {/if}
</div>
